import type { ComponentProps } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { addMonths, dateIsAfter, dateIsBefore, isSameMonth, startOfMonth } from 'lib/datetime/dateUtils'
import BasicCalendar from './BasicCalendar'
import DatePickerDay from './DatePickerDay'
import useMountedEffect from 'hooks/useMountedEffect'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import moment from 'moment'
import VerticalDatePickerHeader from './VerticalDatePickerHeader'
import { type CalendarFocusMonth, resolveVerticalCalendarFocusDate } from './calendarFocusMonth'

export type DatePickerMonthChangeHandler = (direction: 'forwards' | 'backwards', date: Date) => void
type SpacerGap = ComponentProps<typeof VerticalSpacer>['gap']

interface Props {
  value?: Date
  defaultValue?: Date
  min: Date
  max: Date
  className?: string
  defaultMonth?: number
  defaultYear?: number
  dayRender?: (day: Date, params: { disabled: boolean, selected: boolean, onDayClick?: (day: Date) => void }) => React.ReactNode
  onDayClick?: (day: Date) => void
  onMonthChange?: DatePickerMonthChangeHandler
  monthsGap?: SpacerGap
  monthContentGap?: SpacerGap
  hideWeekdays?: boolean
  /**
   * Month to scroll to when the hint changes. Every month is always rendered,
   * so this only changes the viewport — it never selects a date.
   */
  focusMonth?: CalendarFocusMonth
}

const now = new Date()

function VerticalDatePicker(props: Props) {
  const {
    value,
    defaultValue,
    min,
    max,
    className,
    defaultMonth = now.getMonth(),
    defaultYear = now.getFullYear(),
    dayRender,
    onDayClick,
    focusMonth,
    monthsGap = 32,
    monthContentGap = 16,
    hideWeekdays = false,
  } = props
  const [date, setDate] = useState<Date>(value ?? defaultValue ?? new Date(defaultYear, defaultMonth, 1))

  useMountedEffect(() => {
    if (value) {
      // sync what month is on display as our value is set so the value is always visible (as it's set)
      setDate(startOfMonth(value))
    }
  }, [value])

  const focusDate = resolveVerticalCalendarFocusDate(focusMonth, value)
  const focusOrdinal = focusDate
    ? focusDate.getFullYear() * 12 + focusDate.getMonth()
    : undefined
  const focusMonthRef = useRef<HTMLDivElement>(null)
  const lastScrolledFocusOrdinalRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (focusOrdinal === undefined) {
      lastScrolledFocusOrdinalRef.current = undefined
      return
    }

    if (lastScrolledFocusOrdinalRef.current === focusOrdinal) return

    const focusElement = focusMonthRef.current
    if (!focusElement) return

    lastScrolledFocusOrdinalRef.current = focusOrdinal
    focusElement.scrollIntoView({ block: 'start' })
  }, [focusOrdinal])

  const monthStr = `${date.getMonth() + 1}`.padStart(2, '0')

  const defaultDayRender = (date: Date) => {
    const disabled = dateIsBefore(date, min) || dateIsAfter(date, max)
    const dateTimestamp = date.getTime()
    const selected = value?.getTime() === dateTimestamp

    if (dayRender) {
      return dayRender?.(date, { disabled, selected, onDayClick })
    } else {
      return <DatePickerDay
        key={dateTimestamp}
        selected={selected}
        date={date}
        disabled={disabled}
        onClick={onDayClick}
      />
    }
  }

  const period = useMemo(() => {
    const monthsDiffs = moment(max).diff(min, 'months')
    const dates: Array<Date> = [min]

    for (let i = 0; i <= monthsDiffs; i++) {
      const prevDate = dates[i]
      dates.push(addMonths(prevDate, 1))
    }

    return dates
  }, [min, max])

  return (
    <VerticalSpacer
      gap={monthsGap}
      data-testid="date-picker"
      data-yearmonth={`${date.getFullYear()}-${monthStr}`}
      className={className}
    >
      {period.map(d => (
        <VerticalSpacer gap={monthContentGap} key={d.toString()} ref={isSameMonth(d, focusDate) ? focusMonthRef : undefined}>
          <VerticalDatePickerHeader
            currentDate={d}
          />

          <BasicCalendar
            month={d.getMonth()}
            year={d.getFullYear()}
            dayRender={defaultDayRender}
            fixedHeight={false}
            hideWeekdays={hideWeekdays}
          />
        </VerticalSpacer>
      ))}
    </VerticalSpacer>
  )
}

export default React.memo(VerticalDatePicker)
